import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Confirm } from '@client/components/_ui/feedback/Confirm';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { ReadOnlyBanner } from '@client/components/admin/ReadOnlyBanner';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import type { RouterOutputs } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { Alert, Card, Form, Input, Modal, Select, Skeleton, Switch, Table, Tag, Tooltip, Typography } from 'antd';
import {
    Activity,
    AlertTriangle,
    CheckCircle2,
    Copy,
    Globe,
    Mail,
    Plus,
    RefreshCw,
    Search,
    Server,
    Trash2,
    XCircle,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';

const { Text, Title, Paragraph } = Typography;

export const Route = createFileRoute('/admin-console/advanced/inbound-parse/')({
    staticData: {
        breadcrumb: { title: 'Inbound parse' },
        ui: { title: 'Inbound parse configuration' },
    },
    component: InboundParsePage,
});

type HealthStatus = RouterOutputs['admin']['inboundParse']['checkHealth'][number];
type HealthMxStatus = HealthStatus['mxStatus'];

function InboundParsePage() {
    const { canEdit } = useAdminModulePermissions('system');

    return (
        <PageController route={Route}>
            <div className="mx-auto max-w-4xl p-6">
                <Card className="mb-6">
                    <Title level={4} className="!mb-1">
                        Inbound parse configuration
                    </Title>
                    <Text type="secondary">
                        Manage SendGrid inbound parse settings for receiving reply emails. Each hostname requires an MX
                        DNS record pointing to <Text code>mx.sendgrid.net</Text> with priority 10.
                    </Text>
                </Card>

                <ReadOnlyBanner module="system" />
                <SettingsSection canEdit={canEdit} />
                <HealthCheckSection />
                <DnsInstructionsSection />
            </div>
        </PageController>
    );
}

// ── Settings Table ─────────────────────────────────────────────────────────

type ParseSetting = {
    hostname: string;
    url: string;
    spam_check: boolean;
    send_raw: boolean;
    ownedByThisEnv: boolean;
    ownerEnvironment: string;
};

function SettingsSection({ canEdit }: { canEdit: boolean }) {
    const { trpc, trpcClient } = useTrpc();
    const { data, isLoading, refetch } = trpc.admin.inboundParse.listSettings.useQuery();
    const {
        data: healthData,
        isFetching: isCheckingMx,
        refetch: refetchHealth,
    } = trpc.admin.inboundParse.checkHealth.useQuery(undefined, { enabled: false });
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [updatingField, setUpdatingField] = useState<string | null>(null);

    // Build a map of hostname -> MX status from the health data
    const mxStatusMap = new Map(
        healthData?.map((h) => [
            h.hostname.toLowerCase(),
            {
                mxStatus: h.mxStatus,
                mxErrorCode: h.mxErrorCode,
            },
        ]),
    );

    const handleDelete = async (hostname: string) => {
        if (!canEdit) {
            return;
        }

        try {
            message.loading('Deleting...');
            await trpcClient.admin.inboundParse.deleteSetting.mutate({ hostname });
            message.destroy();
            message.success(`Deleted inbound parse setting for ${hostname}`);
            refetch();
        } catch (error) {
            message.destroy();
            errorMessage.show(error);
        }
    };

    const handleToggle = async (hostname: string, field: 'spamCheck' | 'sendRaw', newValue: boolean) => {
        if (!canEdit) {
            return;
        }

        const key = `${hostname}:${field}`;
        try {
            setUpdatingField(key);
            await trpcClient.admin.inboundParse.updateSetting.mutate({
                hostname,
                [field]: newValue,
            });
            message.success(`Updated ${field === 'spamCheck' ? 'spam check' : 'send raw'} for ${hostname}`);
            refetch();
        } catch (error) {
            errorMessage.show(error);
        } finally {
            setUpdatingField(null);
        }
    };

    const handleRefresh = () => {
        refetch();
        refetchHealth();
    };

    const columns = [
        {
            title: 'Hostname',
            dataIndex: 'hostname',
            key: 'hostname',
            render: (hostname: string) => (
                <Space size="small">
                    <Globe size={14} className="text-subtle-foreground" />
                    <Text strong>{hostname}</Text>
                </Space>
            ),
        },
        {
            title: 'Webhook',
            dataIndex: 'url',
            key: 'url',
            width: 160,
            render: (url: string, record: ParseSetting) => {
                const path = extractWebhookPath(url);
                return (
                    <Space size="small">
                        <Tag>{path}</Tag>
                        {!record.ownedByThisEnv && (
                            <Tooltip
                                title={`Owned by ${record.ownerEnvironment}. Mutations are disabled here to avoid affecting another environment (dev/staging/prod share a SendGrid account).`}
                            >
                                <Tag color="default" variant="outlined">
                                    Other env
                                </Tag>
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'MX record',
            key: 'mx',
            width: 110,
            render: (_: unknown, record: ParseSetting) => {
                if (isCheckingMx) return <Tag>Checking...</Tag>;
                const status = mxStatusMap.get(record.hostname.toLowerCase());
                if (status === undefined) return <Tag>Not checked</Tag>;

                switch (status.mxStatus) {
                    case 'configured':
                        return (
                            <Tag color="green" icon={<CheckCircle2 size={12} />} variant="outlined">
                                Valid
                            </Tag>
                        );
                    case 'misconfigured':
                        return (
                            <Tooltip
                                title={`MX records exist for ${record.hostname}, but none point to mx.sendgrid.net.`}
                            >
                                <Tag color="orange" icon={<AlertTriangle size={12} />} variant="outlined">
                                    Wrong target
                                </Tag>
                            </Tooltip>
                        );
                    case 'error':
                        return (
                            <Tooltip
                                title={`Could not verify MX for ${record.hostname}${status.mxErrorCode ? ` (${status.mxErrorCode})` : ''}. Retry the check.`}
                            >
                                <Tag color="gold" icon={<AlertTriangle size={12} />} variant="outlined">
                                    Retry
                                </Tag>
                            </Tooltip>
                        );
                    case 'missing':
                    default:
                        return (
                            <Tooltip title={`No MX record pointing to mx.sendgrid.net found for ${record.hostname}`}>
                                <Tag color="red" icon={<XCircle size={12} />} variant="outlined">
                                    Missing
                                </Tag>
                            </Tooltip>
                        );
                }
            },
        },
        {
            title: 'Spam check',
            dataIndex: 'spam_check',
            key: 'spam_check',
            width: 110,
            render: (enabled: boolean, record: ParseSetting) => (
                <Switch
                    checked={enabled}
                    size="small"
                    disabled={!canEdit || !record.ownedByThisEnv}
                    loading={updatingField === `${record.hostname}:spamCheck`}
                    onChange={(checked) => handleToggle(record.hostname, 'spamCheck', checked)}
                />
            ),
        },
        {
            title: 'Send raw',
            dataIndex: 'send_raw',
            key: 'send_raw',
            width: 110,
            render: (enabled: boolean, record: ParseSetting) => (
                <Switch
                    checked={enabled}
                    size="small"
                    disabled={!canEdit || !record.ownedByThisEnv}
                    loading={updatingField === `${record.hostname}:sendRaw`}
                    onChange={(checked) => handleToggle(record.hostname, 'sendRaw', checked)}
                />
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            render: (_: unknown, record: ParseSetting) => {
                if (!canEdit) return null;
                if (!record.ownedByThisEnv) {
                    return (
                        <Tooltip title={`Owned by ${record.ownerEnvironment} — use that environment to delete.`}>
                            <Button type="danger" size="small" icon={<Trash2 />} disabled />
                        </Tooltip>
                    );
                }
                return (
                    <Confirm
                        title={`Delete ${record.hostname}?`}
                        description="This will remove the inbound parse setting from SendGrid. Emails to this domain will no longer be received."
                        okDanger
                        onOk={() => handleDelete(record.hostname)}
                    >
                        <Button type="danger" size="small" icon={<Trash2 />} />
                    </Confirm>
                );
            },
        },
    ];

    return (
        <Card
            className="mb-6"
            title={
                <Space>
                    <Server size={16} />
                    <span>Parse settings</span>
                </Space>
            }
            extra={
                <Space>
                    <Button
                        type="secondary"
                        size="small"
                        icon={<Search />}
                        loading={isCheckingMx}
                        onClick={() => refetchHealth()}
                    >
                        Verify MX
                    </Button>
                    <Button type="secondary" size="small" icon={<RefreshCw />} onClick={handleRefresh}>
                        Refresh
                    </Button>
                    {canEdit && (
                        <Button type="primary" size="small" icon={<Plus />} onClick={() => setCreateModalOpen(true)}>
                            Add hostname
                        </Button>
                    )}
                </Space>
            }
        >
            {isLoading ? (
                <Skeleton active />
            ) : (
                <Table dataSource={data ?? []} columns={columns} rowKey="hostname" size="small" pagination={false} />
            )}

            <CreateSettingModal
                open={canEdit && createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onSuccess={() => {
                    setCreateModalOpen(false);
                    refetch();
                }}
            />
        </Card>
    );
}

// ── Create Setting Modal ───────────────────────────────────────────────────

interface CreateSettingModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

interface CreateFormValues {
    hostname: string;
    webhookPath: 'inbound' | 'ai-inbox';
    spamCheck: boolean;
    sendRaw: boolean;
}

function CreateSettingModal({ open, onClose, onSuccess }: CreateSettingModalProps) {
    const [form] = Form.useForm<CreateFormValues>();
    const { trpcClient } = useTrpc();
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);
            message.loading('Creating...');

            await trpcClient.admin.inboundParse.createSetting.mutate({
                hostname: values.hostname,
                webhookPath: values.webhookPath,
                spamCheck: values.spamCheck,
                sendRaw: values.sendRaw,
            });

            message.destroy();
            message.success(`Created inbound parse setting for ${values.hostname}`);
            form.resetFields();
            onSuccess();
        } catch (error) {
            message.destroy();
            errorMessage.show(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            title="Add inbound parse hostname"
            open={open}
            onCancel={onClose}
            onOk={handleSubmit}
            confirmLoading={submitting}
            okText="Create"
            destroyOnHidden
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{ webhookPath: 'inbound', spamCheck: true, sendRaw: true }}
            >
                <Form.Item
                    label="Hostname"
                    name="hostname"
                    rules={[
                        { required: true, message: 'Hostname is required' },
                        {
                            pattern:
                                /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*$/,
                            message: 'Invalid hostname format',
                        },
                    ]}
                    help="The subdomain that will receive inbound emails (e.g., sg.curium.app)"
                >
                    <Input placeholder="sg.example.com" />
                </Form.Item>

                <Form.Item
                    label="Webhook endpoint"
                    name="webhookPath"
                    help="Which webhook handler should process emails for this hostname"
                >
                    <Select
                        options={[
                            { label: 'Inbound reply (claim emails)', value: 'inbound' },
                            { label: 'AI inbox', value: 'ai-inbox' },
                        ]}
                    />
                </Form.Item>

                <div className="grid grid-cols-2 gap-4">
                    <Form.Item
                        label="Spam check"
                        name="spamCheck"
                        valuePropName="checked"
                        help="Check for spam before posting"
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item
                        label="Send raw"
                        name="sendRaw"
                        valuePropName="checked"
                        help="Post original MIME content"
                    >
                        <Switch />
                    </Form.Item>
                </div>
            </Form>
        </Modal>
    );
}

// ── Health Check Section ───────────────────────────────────────────────────

function HealthCheckSection() {
    const { trpc } = useTrpc();
    const { data, isLoading, refetch, isFetching } = trpc.admin.inboundParse.checkHealth.useQuery(undefined, {
        enabled: false,
    });

    return (
        <Card
            className="mb-6"
            title={
                <Space>
                    <Activity size={16} />
                    <span>Health check</span>
                </Space>
            }
            extra={
                <Button type="secondary" size="small" icon={<Search />} loading={isFetching} onClick={() => refetch()}>
                    Check all
                </Button>
            }
        >
            {isLoading || isFetching ? (
                <Skeleton active />
            ) : data ? (
                <div className="space-y-3">
                    {data.map((status) => (
                        <HealthStatusRow key={status.hostname} status={status} />
                    ))}
                    {data.length === 0 && <Text type="secondary">No inbound parse settings configured.</Text>}
                </div>
            ) : (
                <Text type="secondary">Click "Check all" to run a health check on all inbound parse settings.</Text>
            )}
        </Card>
    );
}

function HealthStatusRow({ status }: { status: HealthStatus }) {
    const healthColour = {
        healthy: 'green' as const,
        degraded: 'orange' as const,
        misconfigured: 'red' as const,
        unknown: 'gold' as const,
    };

    const healthIcon = {
        healthy: <CheckCircle2 size={14} />,
        degraded: <AlertTriangle size={14} />,
        misconfigured: <XCircle size={14} />,
        unknown: <AlertTriangle size={14} />,
    };

    const mxIndicatorByStatus: Record<HealthMxStatus, { icon: ReactNode; label: string }> = {
        configured: {
            icon: <CheckCircle2 size={12} className="text-success" />,
            label: 'MX record',
        },
        missing: {
            icon: <XCircle size={12} className="text-error" />,
            label: 'MX record',
        },
        misconfigured: {
            icon: <AlertTriangle size={12} className="text-warning" />,
            label: 'MX record',
        },
        error: {
            icon: <AlertTriangle size={12} className="text-warning" />,
            label: 'MX lookup',
        },
    };
    const mxIndicator = mxIndicatorByStatus[status.mxStatus];

    return (
        <div className="rounded-md border border-separator p-3">
            <div className="mb-2 flex items-center justify-between">
                <Space>
                    <Globe size={14} className="text-subtle-foreground" />
                    <Text strong>{status.hostname}</Text>
                    <Tag
                        color={healthColour[status.overallHealth]}
                        icon={healthIcon[status.overallHealth]}
                        variant="outlined"
                    >
                        {status.overallHealth}
                    </Tag>
                </Space>
            </div>

            <div className="flex flex-wrap gap-4 text-xs">
                <Space size="small">
                    {mxIndicator.icon}
                    <Text type="secondary">{mxIndicator.label}</Text>
                </Space>

                <Space size="small">
                    {status.webhookUrlValid ? (
                        <CheckCircle2 size={12} className="text-success" />
                    ) : (
                        <XCircle size={12} className="text-error" />
                    )}
                    <Text type="secondary">Webhook URL</Text>
                </Space>

                <Space size="small">
                    {status.parseConfigured ? (
                        <CheckCircle2 size={12} className="text-success" />
                    ) : (
                        <XCircle size={12} className="text-error" />
                    )}
                    <Text type="secondary">Parse configured</Text>
                </Space>
            </div>

            {status.referencingOrgs.length > 0 && (
                <div className="mt-2">
                    <Text type="secondary" className="text-xs">
                        Referenced by:{' '}
                        {status.referencingOrgs.map((org, i) => (
                            <span key={String(org.id)}>
                                {i > 0 && ', '}
                                <Text className="text-xs">{org.name}</Text>
                            </span>
                        ))}
                    </Text>
                </div>
            )}

            {status.mxStatus === 'missing' && (
                <Alert
                    type="error"
                    showIcon
                    icon={<XCircle size={14} />}
                    title={`MX record not found. Add an MX record for ${status.hostname} pointing to mx.sendgrid.net with priority 10.`}
                    className="mt-2"
                />
            )}

            {status.mxStatus === 'misconfigured' && (
                <Alert
                    type="error"
                    showIcon
                    icon={<AlertTriangle size={14} />}
                    title={`MX records were found for ${status.hostname}, but none point to mx.sendgrid.net. Update the MX record target and keep priority 10.`}
                    className="mt-2"
                />
            )}

            {status.mxStatus === 'error' && (
                <Alert
                    type="warning"
                    showIcon
                    icon={<AlertTriangle size={14} />}
                    title={`Could not verify MX for ${status.hostname}${status.mxErrorCode ? ` (${status.mxErrorCode})` : ''}. Retry the health check instead of changing DNS yet.`}
                    className="mt-2"
                />
            )}
        </div>
    );
}

// ── DNS Instructions ───────────────────────────────────────────────────────

function DnsInstructionsSection() {
    const { trpc } = useTrpc();
    const { data: settings } = trpc.admin.inboundParse.listSettings.useQuery();
    const [emailModalOpen, setEmailModalOpen] = useState(false);

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text);
        message.success('Copied to clipboard');
    };

    return (
        <Card
            title={
                <Space>
                    <Globe size={16} />
                    <span>DNS configuration guide</span>
                </Space>
            }
            extra={
                <Button type="secondary" size="small" icon={<Mail />} onClick={() => setEmailModalOpen(true)}>
                    Email instructions
                </Button>
            }
        >
            <Paragraph type="secondary">
                For each inbound parse hostname, add the following MX DNS record in your domain registrar or DNS
                provider (e.g., Cloudflare):
            </Paragraph>

            <div className="rounded-md border border-separator bg-muted p-4">
                <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                        <Text type="secondary" className="text-xs">
                            Type
                        </Text>
                        <div>
                            <Text strong>MX</Text>
                        </div>
                    </div>
                    <div>
                        <Text type="secondary" className="text-xs">
                            Host / Name
                        </Text>
                        <div>
                            <Text strong>{'<subdomain>'}</Text>
                        </div>
                    </div>
                    <div>
                        <Text type="secondary" className="text-xs">
                            Value / Points to
                        </Text>
                        <div className="flex items-center gap-1">
                            <Text strong>mx.sendgrid.net</Text>
                            <Tooltip title="Copy">
                                <Button
                                    type="tertiary"
                                    size="small"
                                    icon={<Copy size={12} />}
                                    onClick={() => handleCopy('mx.sendgrid.net')}
                                />
                            </Tooltip>
                        </div>
                    </div>
                    <div>
                        <Text type="secondary" className="text-xs">
                            Priority
                        </Text>
                        <div>
                            <Text strong>10</Text>
                        </div>
                    </div>
                </div>
            </div>

            <Paragraph type="secondary" className="mt-4">
                Example: For <Text code>sg.curium.app</Text>, create an MX record with host <Text code>sg</Text> on the{' '}
                <Text code>curium.app</Text> domain, pointing to <Text code>mx.sendgrid.net</Text> with priority{' '}
                <Text code>10</Text>.
            </Paragraph>

            <Alert
                type="info"
                showIcon
                title="DNS changes can take up to 48 hours to propagate, though most changes are visible within minutes."
                className="mt-2"
            />

            <EmailInstructionsModal
                open={emailModalOpen}
                onClose={() => setEmailModalOpen(false)}
                hostnames={settings?.filter((s) => s.ownedByThisEnv).map((s) => s.hostname) ?? []}
            />
        </Card>
    );
}

// ── Email Instructions Modal ───────────────────────────────────────────────

// A conservative cap for the *encoded* mailto: URI length. RFC doesn't define a hard limit but
// real-world mail clients and browsers truncate or refuse URIs above ~2000 chars. Measuring
// against the raw body undercounts: `encodeURIComponent` expands newlines to `%0A` and spaces
// to `%20` (3 bytes each), so a 1267-char line-oriented body becomes a ~2100-char URI once
// assembled with `mailto:` + subject + recipient.
const MAILTO_URI_CHAR_BUDGET = 1900;
const EMAIL_INSTRUCTIONS_SUBJECT = 'DNS configuration required — MX records for inbound email';

function buildMailtoUri(recipient: string, body: string): string {
    return `mailto:${encodeURIComponent(recipient)}?subject=${encodeURIComponent(
        EMAIL_INSTRUCTIONS_SUBJECT,
    )}&body=${encodeURIComponent(body)}`;
}

function buildEmailInstructionsBody(hostnames: string[]): string {
    const hostnameList =
        hostnames.length > 0
            ? hostnames
                  .map((h) => {
                      return `  - Host/FQDN: ${h}\n    Type: MX\n    Value: mx.sendgrid.net\n    Priority: 10`;
                  })
                  .join('\n\n')
            : '  (No hostnames configured yet)';

    return `Hi,

We need MX DNS records configured for our inbound email handling. Please add the following MX records:

${hostnameList}

Each record should point to mx.sendgrid.net with priority 10.

DNS changes can take up to 48 hours to propagate, though most are visible within minutes. Please let us know once the records have been added so we can verify them.

Thank you`;
}

function EmailInstructionsModal({
    open,
    onClose,
    hostnames,
}: {
    open: boolean;
    onClose: () => void;
    hostnames: string[];
}) {
    const [recipientEmail, setRecipientEmail] = useState('');

    const instructionsBody = buildEmailInstructionsBody(hostnames);
    // Larger environments can hold hundreds of parse-settings rows; serialising every hostname
    // into a single mailto: will silently truncate or refuse to open in browser/mail clients
    // once the encoded URI exceeds roughly two thousand characters. Measure the *assembled,
    // encoded* URI rather than the raw body — newlines / spaces / punctuation triple in size
    // after encoding, so a sub-budget body can still produce an over-budget URI. Budget against
    // a realistic recipient (empty when the user hasn't typed one yet) plus an 80-char reserve
    // so the decision is stable as they finish typing the address.
    const RECIPIENT_RESERVE = 80;
    const probeRecipient = recipientEmail || 'x'.repeat(RECIPIENT_RESERVE);
    const fullMailtoUri = buildMailtoUri(probeRecipient, instructionsBody);
    const exceedsMailtoBudget = fullMailtoUri.length > MAILTO_URI_CHAR_BUDGET;

    const copyInstructionsToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(instructionsBody);
            message.success('DNS instructions copied to clipboard');
            return true;
        } catch {
            message.error('Could not copy to clipboard — select the text below and copy manually');
            return false;
        }
    };

    const handleOpenEmail = async () => {
        if (!recipientEmail) {
            message.warning('Please enter a recipient email address');
            return;
        }

        if (exceedsMailtoBudget) {
            // Copy the full instructions to the clipboard and open a lightweight mailto so the
            // mail client always launches even when the full body wouldn't fit in a URI.
            const copied = await copyInstructionsToClipboard();
            const pointerBody = copied
                ? 'The full DNS instructions (including hostnames, record type, value, and priority) have been copied to your clipboard — please paste them into the message body before sending.'
                : 'Please paste the DNS instructions shown in the Curium admin console into this message before sending.';
            window.open(buildMailtoUri(recipientEmail, pointerBody), '_blank');
        } else {
            window.open(buildMailtoUri(recipientEmail, instructionsBody), '_blank');
        }

        onClose();
        setRecipientEmail('');
    };

    return (
        <Modal
            title="Email DNS instructions"
            open={open}
            onCancel={onClose}
            onOk={handleOpenEmail}
            okText="Open email"
            destroyOnHidden
        >
            <Paragraph type="secondary" className="mb-4">
                This will open your email client with pre-filled DNS configuration instructions for all configured
                inbound parse hostnames.
            </Paragraph>
            {exceedsMailtoBudget && (
                <Alert
                    type="info"
                    showIcon
                    className="mb-4"
                    title="Large hostname list"
                    description="Too many hostnames to fit in a mailto link. Opening the email will copy the full instructions to your clipboard so you can paste them into the message body."
                />
            )}
            <Form layout="vertical">
                <Form.Item label="Recipient email">
                    <Input
                        placeholder="admin@example.com"
                        type="email"
                        value={recipientEmail}
                        onChange={(e) => setRecipientEmail(e.target.value)}
                        onPressEnter={handleOpenEmail}
                    />
                </Form.Item>
            </Form>
            <div className="mb-3 flex items-center justify-between">
                <Text type="secondary" className="text-xs">
                    Instructions preview ({hostnames.length} hostname{hostnames.length === 1 ? '' : 's'})
                </Text>
                <Button
                    type="tertiary"
                    size="small"
                    icon={<Copy size={14} />}
                    onClick={() => void copyInstructionsToClipboard()}
                >
                    Copy instructions
                </Button>
            </div>
            <Input.TextArea
                value={instructionsBody}
                readOnly
                autoSize={{ minRows: 6, maxRows: 12 }}
                className="mb-3 font-mono text-xs"
            />
            {hostnames.length > 0 && (
                <div className="rounded-md bg-muted p-3">
                    <Text type="secondary" className="text-xs">
                        Hostnames to include:
                    </Text>
                    <div className="mt-1 flex flex-wrap gap-1">
                        {hostnames.map((h) => (
                            <Tag key={h}>{h}</Tag>
                        ))}
                    </div>
                </div>
            )}
        </Modal>
    );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function extractWebhookPath(url: string): string {
    try {
        // Check the full URL text — covers both pathname and ngrok-style URLs
        if (url.includes('/ai-inbox') || url.includes('ai-inbox')) return 'ai-inbox';
        if (url.includes('/inbound') || url.includes('claim-inbound')) return 'inbound';
        return 'unknown';
    } catch {
        return 'unknown';
    }
}
